import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import type { ApiError } from '../models/error.model';
import { ErrorCode, normalizeErrorCode } from '../models/error.model';

export interface ParsedError {
  code: string;
  /**
   * The backend's own message, or `''` when it sent none.
   *
   * Deliberately not defaulted to a placeholder here: a hardcoded default would
   * be untranslatable English and would make every caller's `|| t.something`
   * fallback unreachable. Callers own the localized fallback.
   */
  message: string;
  status: number;
  metadata?: Record<string, unknown>;
  type: 'email' | 'password' | 'network' | 'server' | 'unknown';
}

@Injectable({
  providedIn: 'root',
})
export class ErrorHandler {
  parseError(error: HttpErrorResponse): ParsedError {
    const status = error.status;
    const apiError = error.error as ApiError;
    // Backend error codes may arrive as a JSON number or string; normalize to a
    // string so comparisons against the string-valued ErrorCode enum stay reliable.
    const code = normalizeErrorCode(apiError?.code);

    // Default error structure
    const parsedError: ParsedError = {
      code: code ?? 'UNKNOWN',
      message: apiError?.message ?? '',
      status,
      metadata: apiError?.metadata,
      type: 'unknown',
    };

    // Categorize error by code
    switch (code) {
      case ErrorCode.MERCHANT_NOT_FOUND:
        parsedError.type = 'email';
        break;
      case ErrorCode.INVALID_PASSWORD:
        parsedError.type = 'password';
        break;
      default:
        // Fallback to status code
        if (status === 404) {
          parsedError.type = 'email';
        } else if (status === 401 || status === 400) {
          parsedError.type = 'password';
        } else if (status === 500) {
          parsedError.type = 'server';
        } else {
          parsedError.type = 'network';
        }
    }

    return parsedError;
  }

  /**
   * Read `metadata.remaining_attempts` as a usable count.
   *
   * The backend sends this field either as a JSON number or as a string, and it
   * sends an empty string once the retry budget is exhausted. Returns null for
   * anything that is not a non-negative integer, so callers never have to guard
   * against NaN before rendering the value.
   */
  getRemainingAttempts(error: ParsedError): number | null {
    const attempts = error.metadata?.['remaining_attempts'];

    if (typeof attempts === 'number') {
      return Number.isInteger(attempts) && attempts >= 0 ? attempts : null;
    }

    if (typeof attempts === 'string' && attempts.trim() !== '') {
      const parsed = Number.parseInt(attempts, 10);
      return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
    }

    return null;
  }
}
